import type { ReactNode } from 'react';
import { PanelSection } from '../ui/PanelSection.js';

/*
  What a panel opens with.

  ONE TABLE, NOT A BOOLEAN AT EACH CALL SITE, and the argument is not tidiness. A default is a product
  decision — it decides what a reader sees before they have said anything — and twenty-one booleans
  spread across eleven files is a decision nobody ever reads together. It is how "the person panel
  folds the only box you can type in" ships without anybody noticing. This file is the artefact a
  reviewer can argue with.

  It also has to live in `panels/` rather than in `ui/`. `ui/index.tsx` states the boundary: "what does
  NOT belong here is anything that knows about a carve-out — a bucket, a perspective, a stage". A list
  containing `person-stranded`, `group-target-place` and `mitigation-plan` is exactly that, so
  `PanelSection` stays domain-free and takes its answer as a prop.

  FAIL OPEN. This is a DENY list, so a section added tomorrow by somebody who never finds this file is
  VISIBLE. The inverse — naming the sections that open — makes a forgotten one invisible, which is the
  difference between a review comment and a bug report from a workshop.

  THE RULE THIS WAS AUTHORED WITH is "the first two sections of a panel stay open": the identity, then
  the primary reading or decision. It is a good way to WRITE a table and a bad way to EVALUATE one,
  because position is not a stable property here — `person-stage` exists only for a Carve-out, and
  `group-pivot-note` / `group-restructure` / the target unit controls are three branches of one
  ternary. A section that opened or folded depending on which person you clicked would not be a
  preference, it would be a glitch. So the rule produced the first draft and the exceptions below
  overrule it, each with its reason.
*/
const CLOSED = new Set<string>([
  /* --- a person --------------------------------------------------------------------------------
     `person-panel` and `person-bucket` are who they are and what you decided. `person-stage` and
     `buildup-stage` are conditional on the decision you just took — they exist only when they are the
     immediate next question — so they stay open when they exist at all. */
  'person-stranded',
  'person-target',
  'person-heads',
  'buildup-fields',

  /* --- a group ---------------------------------------------------------------------------------
     `group-bulk` is open despite being third: it is the group's equivalent of `person-bucket`, and
     folding the one thing you do TO a group would be folding the panel's purpose. */
  'group-pivot-note',
  'group-restructure',
  'group-target-unit',
  'group-target-place',

  /* --- phase 1, the data ------------------------------------------------------------------------ */
  'data-quality',
  'data-assumptions',
  'data-heads',

  /* --- phase 2, the perimeter -------------------------------------------------------------------
     `perimeter-manual` is third and stays OPEN. The panel's own prose four lines above it tells the
     reader to "designate the perimeter manually below" (`PerimeterPanel.tsx:201`) — folding the
     section that sentence points at makes the application's own instruction wrong. */
  'target-progress',

  /* --- phase 3, transfers ----------------------------------------------------------------------- */
  'current-transfers',
  'build-ups',

  /* --- phase 4, TSAs ---------------------------------------------------------------------------- */
  'rightsizing',

  /* --- RemainCo --------------------------------------------------------------------------------- */
  'mitigation-plan',

  /* --- the target org --------------------------------------------------------------------------- */
  'target-add-top',
  'target-rollup',

  /* --- a multi-selection ------------------------------------------------------------------------ */
  'selection-field-edit',
  'selection-members',

  /* --- the panel's tail -------------------------------------------------------------------------
     `activity` stays open: live awareness of what colleagues are doing is the point of a shared tool,
     and it is the one section whose value is that you did not go looking for it. `whos-here` folds,
     because most of the time it says "Just you right now". */
  'whos-here',
]);

/*
  THE TWO OTHER OVERRULES, recorded here because a rule with unexplained exceptions rots into a rule
  nobody trusts:

  `data-target` is sixth in the data panel and stays open. It holds `target-toggle`, the only control
  in the product that adds the Target phase at all — somebody who has not seen this application before
  will not find a whole phase behind a fold.

  `person-fields` is sixth by source order and stays open. It is the only place in that panel where
  anybody TYPES, and it is the section for which the authoring rule gives a different answer depending
  on the bucket of the person you happened to click. `person-heads` is folded in its place.
*/

/**
 * A side-panel section, with this table's answer for whether it starts open.
 *
 * Panels import this and never name a boolean, which is what keeps the policy in one file.
 */
export function Section({
  testId,
  title,
  actions,
  attrs,
  children,
}: {
  testId: string;
  title: ReactNode;
  actions?: ReactNode;
  attrs?: Record<string, string | number | undefined>;
  children: ReactNode;
}): JSX.Element {
  return (
    <PanelSection
      testId={testId}
      title={title}
      actions={actions}
      attrs={attrs}
      defaultOpen={!CLOSED.has(testId)}
    >
      {children}
    </PanelSection>
  );
}

/**
 * Exported for this table's own test, which is the only thing that stops it rotting.
 *
 * The failure mode is silent: a renamed section leaves a dead entry that folds nothing and a live
 * section that nobody decided about. Naming ids in a `Set<string>` buys no help from the compiler,
 * so the check has to be a test.
 */
export const CLOSED_BY_DEFAULT: ReadonlySet<string> = CLOSED;
